import React from "react";
import { FiSettings } from "react-icons/fi";
import { useLocation } from "@reach/router";
import { BaseMemberPaths } from "newui/utils/navigation/navigation.types";
import { useCurrentUserHasFeatureFlag, useHasNotificationsConfiguration, useHasRfxSettings } from "hooks/featureFlags";
import { CurrentAccountType, useProfileContext } from "newui/features/Auth/context/ProfileContext";
import { ExpandableMenuItem, ExpandableMenuChildItem } from "./ExpandableMenuItem";
import { useIsCurrentUserAdmin } from "hooks/auth";
import { useModuleCan } from "newui/features/RolesPermissions/hooks/useModuleCan";
import { useRfxSettingsTabs } from "newui/features/Settings/hooks/useRfxSettingsTabs";
import { RBAC_MODULE_KEYS } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { FeatureFlagType } from "newui/features/Auth/types/FeatureFlag.type";

interface SettingsExpandableMenuProps {
	isCollapsed: boolean;
	paths: BaseMemberPaths;
	onNavigate?: () => void;
}

const STORAGE_KEY = "settingsMenu_expanded";

export const SettingsExpandableMenu: React.FC<SettingsExpandableMenuProps> = ({
	isCollapsed,
	paths,
	onNavigate,
}) => {
	const isAdminUser = useIsCurrentUserAdmin();
	/*
	 * PRO-709 / PRO-713 / PRO-715: the settings module was SPLIT
	 * (2026_09_11_200000_seed_settings_module_split). `settings` is now the
	 * residual, System-Administrator-only company-administration module — it is
	 * `none` for all eleven other base roles — and each settings surface carries
	 * its own key. The pages were moved onto those keys, which is why reaching
	 * them by URL works, but this menu was left gating visibility on `settings`,
	 * so Procurement Lead/Support and Bidder Lead/Support got no Settings entry
	 * at all despite holding `rfx_settings_*` / `pdf_report_settings`.
	 *
	 * Each child is now gated on the module that actually governs its page, and
	 * the menu shows whenever at least one child is reachable. `isAdminUser`
	 * (`administers_company_rbac`) stays as a bypass so legacy company admins,
	 * who may carry no RBAC module grants, keep the access they have today.
	 */
	const settings = useModuleCan(RBAC_MODULE_KEYS.settings);
	const pdfReportSettings = useModuleCan(RBAC_MODULE_KEYS.pdfReportSettings);
	// The RFx Settings page's OWN answer about what it can show this user — see
	// useRfxSettingsTabs for why asking that question separately here was a bug.
	const rfxSettingsTabs = useRfxSettingsTabs();
	const hasRfxSettings = useHasRfxSettings();
	const profileContext = useProfileContext();
	const isSupplierReportBrandingEnabled = useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_REPORT_BRANDING_FOR_SUPPLIER);
	const hasNotificationsConfiguration = useHasNotificationsConfiguration();
	const isProcurer = profileContext?.currentProfile === CurrentAccountType.PROCURER;
	const location = useLocation();
	const currentPath = location.pathname;

	const accountSettingsLink = paths.accountSettings.userManagement;
	const usersLink = paths.accountSettings.users.list;
	const rfxSettingsLink = paths.rfxSettings;
	const pdfSettingsLink = paths.pdfSettings;
	const notificationSettingsLink = paths.notificationSettings;
	const rolesLink = paths.accountSettings.roles;
	const auditLink = paths.accountSettings.audit;

	const isAccountSettingsActive = currentPath.includes("account-settings") && !currentPath.includes("account-settings/users") && !currentPath.includes("account-settings/roles") && !currentPath.includes("account-settings/audit");
	const isUsersActive = currentPath.includes("account-settings/users");
	const isRfxSettingsActive = currentPath.includes("rfx-settings");
	const isPdfSettingsActive = currentPath.includes("pdf-settings");
	const isNotificationSettingsActive = currentPath.includes("settings/notifications");
	const isRolesActive = currentPath.includes("account-settings/roles");
	const isAuditActive = currentPath.includes("account-settings/audit");

	/*
	 * The three RFx settings surfaces share one menu entry, so the entry shows
	 * exactly when the page has at least one tab to put behind it.
	 *
	 * NO `isAdminUser` BYPASS HERE, unlike the administrative children below.
	 * The page grants nothing on `administers_company_rbac`, so bypassing here
	 * advertised a door that opened onto "Access denied" for any legacy company
	 * admin carrying no RBAC module grants — the reported bug. The menu follows
	 * the page; it does not make a promise of its own.
	 */
	const hasRfxSettingsSurface = (hasRfxSettings && isProcurer) || !isProcurer;
	const showRfxSettings = hasRfxSettingsSurface && rfxSettingsTabs.tabs.length > 0;
	const showPdfSettings = isSupplierReportBrandingEnabled && (isAdminUser || pdfReportSettings.canView);
	// Notification configuration is one of the surfaces `settings` still governs.
	const showNotificationSettings = hasNotificationsConfiguration && (isAdminUser || settings.canView);

	// A non-admin has no administrative children, so without a reachable child the
	// menu would be an empty shell pointing at a page they cannot open.
	if (!isAdminUser && !showRfxSettings && !showPdfSettings && !showNotificationSettings) {
		return null;
	}

	const firstReachableLink = (): string => {
		if (showRfxSettings) {
			return rfxSettingsLink;
		}
		if (showPdfSettings) {
			return pdfSettingsLink;
		}
		return notificationSettingsLink;
	};

	return (
		<ExpandableMenuItem
			icon={<FiSettings size={24} />}
			label="Settings"
			storageKey={STORAGE_KEY}
			isCollapsed={isCollapsed}
			collapsedLink={isAdminUser ? accountSettingsLink : firstReachableLink()}
			testId="settings-menu"
			onNavigate={onNavigate}
		>
			{isAdminUser && (
				<>
					<ExpandableMenuChildItem
						name="Account Settings"
						link={accountSettingsLink}
						isActive={isAccountSettingsActive}
						testId="account-settings-child"
						tourName="AccountSettings"
						onNavigate={onNavigate}
					/>
					<ExpandableMenuChildItem
						name="User Management"
						link={usersLink}
						isActive={isUsersActive}
						testId="users-child"
						onNavigate={onNavigate}
						tourName="UserManagement"
					/>
					<ExpandableMenuChildItem
						name="Roles & Permissions"
						link={rolesLink}
						isActive={isRolesActive}
						testId="roles-permissions-child"
						onNavigate={onNavigate}
					/>
					<ExpandableMenuChildItem
						name="Audit Trail"
						link={auditLink}
						isActive={isAuditActive}
						testId="audit-trail-child"
						onNavigate={onNavigate}
					/>
				</>
			)}
			{showRfxSettings && (
				<ExpandableMenuChildItem
					name="RFx Settings"
					link={rfxSettingsLink}
					isActive={isRfxSettingsActive}
					testId="rfx-settings-child"
					onNavigate={onNavigate}
				/>
			)}
			{showPdfSettings && (
				<ExpandableMenuChildItem
					name="PDF Report Settings"
					link={pdfSettingsLink}
					isActive={isPdfSettingsActive}
					testId="pdf-settings-child"
					onNavigate={onNavigate}
				/>
			)}
			{showNotificationSettings && (
				<ExpandableMenuChildItem
					name="Notification Settings"
					link={notificationSettingsLink}
					isActive={isNotificationSettingsActive}
					testId="notification-settings-child"
					onNavigate={onNavigate}
				/>
			)}
		</ExpandableMenuItem>
	);
};
